import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { trpc, type RouterOutputs } from '@org/clients/school';
import { useFeatureFlags } from '@org/ui';
import { Selo, seloCharacteristic } from '../../components/selo';
import { Portrait, initialOf } from '../../components/portrait';
import { FeatureFlagGate } from '../../components/feature-flag-gate';

// -- Responsive hook (matches journey.tsx convention) --
const useWidth = () => {
  const [w, setW] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 640,
  );
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
};

export const Route = createFileRoute('/_app/mural')({
  component: GatedMuralPage,
});

function GatedMuralPage() {
  return (
    <FeatureFlagGate flag="journey">
      <MuralPage />
    </FeatureFlagGate>
  );
}

// -- Feminist color palette (local copy — mural must not import from journey.tsx) --
const P = {
  maxRed: '#D41920',
  sizzle: '#E84B5A',
  charm: '#F2A0C4',
  baby: '#F8D7DD',
  black: '#1A1018',
  white: '#FFFFFF',
  cream: '#FFF8F5',
  offWhite: '#FEF3EF',
  blush: '#FDF0ED',
  roseMist: '#F9E4E0',
  warmGray: '#756560',
  darkText: '#2D1A1F',
  midText: '#6B4F55',
  lightBorder: '#F0DAD5',
  green: '#1E7A46',
  shadow: 'rgba(180, 80, 80, 0.12)',
  shadowDeep: 'rgba(120, 40, 40, 0.18)',
};

const oswald = "'Oswald', sans-serif";
const dmSerif = "'DM Serif Text', serif";

// -- Rotation cycle for mini-cartaz tilt --
const MINI_ROTATIONS = [-2.2, 1.6, -1, 2, -1.6, 1.2];

// -- Reduced-motion preference (checked at call time for JS-driven scrolls) --
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// -- Types --
// Output type straight from the router (ReturnType<typeof useQuery> is
// unreliable under a full p2e tsc run — see journey.tsx's same pattern).
type MuralData = RouterOutputs['progress']['getStudentMural'];
type MuralLevel = MuralData['levels'][number];
type MuralUnit = NonNullable<MuralLevel['units']>[number];
type MuralExtra = MuralData['extras'][number];

// -- Injected styles (hover/focus affordances, honoring reduced motion) --
const MURAL_STYLES = `
.mural-card-interactive { transition: transform 0.15s ease, box-shadow 0.15s ease; }
.mural-card-interactive:hover {
  transform: rotate(0deg) scale(1.04) !important;
  box-shadow: 8px 12px 26px rgba(120,40,40,0.25) !important;
}
.mural-card-interactive:focus-visible {
  outline: 3px solid ${P.charm};
  outline-offset: 2px;
}
.mural-chip { transition: transform 0.12s ease, box-shadow 0.12s ease; }
.mural-chip:hover { transform: translateY(-1px); box-shadow: 0 3px 10px ${P.shadowDeep}; }
.mural-chip:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.mural-header-link:hover { text-decoration: underline; }
.mural-header-link:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.mural-shelf { scrollbar-width: thin; -webkit-overflow-scrolling: touch; }
.mural-shelf-card { transition: transform 0.15s ease, box-shadow 0.15s ease; }
.mural-shelf-card:hover { transform: translateY(-3px); box-shadow: 6px 10px 22px rgba(120,40,40,0.22) !important; }
.mural-shelf-card:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) {
  .mural-card-interactive, .mural-chip, .mural-shelf-card { transition: none; }
  .mural-card-interactive:hover { transform: none !important; }
  .mural-chip:hover, .mural-shelf-card:hover { transform: none; }
}
::-webkit-scrollbar { width: 6px; }
::-webkit-scrollbar-track { background: ${P.cream}; }
::-webkit-scrollbar-thumb { background: ${P.charm}88; border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: ${P.charm}; }
`;

// -- Poster color combos: same 6-cycle as the journey wall --
const POSTER_STYLES: Array<{
  bg: string;
  text: string;
  tape: string;
  seloBg: string; // pink/cream posters get Maximum Red so the selo stays legible
}> = [
  { bg: P.maxRed, text: P.white, tape: 'rgba(255,255,255,0.15)', seloBg: P.maxRed },
  { bg: P.baby, text: P.maxRed, tape: 'rgba(212,25,32,0.1)', seloBg: P.maxRed },
  { bg: P.black, text: P.white, tape: 'rgba(255,255,255,0.15)', seloBg: P.black },
  { bg: P.charm, text: P.darkText, tape: 'rgba(212,25,32,0.1)', seloBg: P.maxRed },
  { bg: P.sizzle, text: P.white, tape: 'rgba(255,255,255,0.15)', seloBg: P.sizzle },
  { bg: P.cream, text: P.maxRed, tape: 'rgba(212,25,32,0.1)', seloBg: P.maxRed },
];

function getPosterStyle(index: number) {
  return POSTER_STYLES[index % POSTER_STYLES.length]!;
}

function getRotation(index: number) {
  return MINI_ROTATIONS[index % MINI_ROTATIONS.length]!;
}

function surnameOf(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/);
  return parts.length > 0 ? parts[parts.length - 1]! : '';
}

// Keyboard/click activation props for interactive cards & headers.
function pressable(onActivate: () => void) {
  return {
    role: 'button' as const,
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    },
  };
}

// Renders a translated stats segment with its number/level highlighted in red.
function StatSegment({
  text,
  highlight,
}: {
  text: string;
  highlight: string;
}) {
  const idx = text.indexOf(highlight);
  if (idx < 0) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <span style={{ color: P.maxRed, fontWeight: 700 }}>{highlight}</span>
      {text.slice(idx + highlight.length)}
    </span>
  );
}

// -- Small tape strip on top of a completed mini-cartaz --
function MiniTape({ color }: { color: string }) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        top: -6,
        left: '50%',
        transform: 'translateX(-50%) rotate(-4deg)',
        width: 34,
        height: 12,
        background: color,
        borderRadius: 1,
      }}
    />
  );
}

const CARD_W = 96;
const CARD_H = 126;

// -- Completed unit: full-color mini poster with the selo stamped on --
function CompletedMiniCartaz({
  unit,
  index,
  onOpen,
}: {
  unit: MuralUnit;
  index: number;
  onOpen: () => void;
}) {
  const s = getPosterStyle(index);
  const surname = surnameOf(unit.woman?.name);
  return (
    <div
      {...pressable(onOpen)}
      className="mural-card-interactive"
      aria-label={unit.name ?? undefined}
      style={{
        width: CARD_W,
        height: CARD_H,
        position: 'relative',
        background: s.bg,
        color: s.text,
        borderRadius: 3,
        boxShadow: '4px 5px 14px rgba(120,40,40,.18)',
        transform: `rotate(${getRotation(index)}deg)`,
        padding: '14px 9px 8px',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        // no overflow hidden — the selo protrudes past the corner
      }}
    >
      <MiniTape color={s.tape} />
      <div
        style={{
          fontFamily: oswald,
          fontWeight: 700,
          fontSize: 11,
          lineHeight: 1.25,
          overflow: 'hidden',
          flex: 1,
          textTransform: 'uppercase',
        }}
      >
        {unit.quote ?? unit.name ?? ''}
      </div>
      {surname && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            marginTop: 4,
            minWidth: 0,
          }}
        >
          {/* Her collage portrait; her initial when no photo */}
          <Portrait
            url={unit.woman?.portraitUrl}
            initial={initialOf(unit.woman?.name)}
            name={unit.woman?.name}
            color={s.text}
            size={28}
          />
          <div
            style={{
              fontFamily: oswald,
              fontSize: 7,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              opacity: 0.85,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {'— ' + surname}
          </div>
        </div>
      )}
      <Selo
        size={34}
        symbol={unit.woman?.symbol}
        label={seloCharacteristic(unit.badge, unit.name)}
        background={s.seloBg}
        style={{ position: 'absolute', top: -11, right: -11 }}
      />
    </div>
  );
}

// -- In-progress unit: white card with conic progress ring --
function InProgressMiniCartaz({
  unit,
  index,
  onOpen,
}: {
  unit: MuralUnit;
  index: number;
  onOpen: () => void;
}) {
  const total = unit.classesTotal > 0 ? unit.classesTotal : 1;
  const pct = Math.min(100, Math.round((unit.classesCompleted / total) * 100));
  const surname = surnameOf(unit.woman?.name);
  return (
    <div
      {...pressable(onOpen)}
      className="mural-card-interactive"
      aria-label={unit.name ?? undefined}
      style={{
        width: CARD_W,
        height: CARD_H,
        position: 'relative',
        background: P.white,
        color: P.black,
        border: `2px solid ${P.black}`,
        borderRadius: 3,
        boxShadow: '4px 5px 14px rgba(120,40,40,.18)',
        transform: `rotate(${getRotation(index)}deg)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        cursor: 'pointer',
        padding: 8,
      }}
    >
      <div
        aria-hidden
        style={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          background: `conic-gradient(${P.black} ${pct}%, #EFE0DC 0)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: P.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: oswald,
            fontSize: 9,
            fontWeight: 700,
          }}
        >
          {unit.classesCompleted}/{unit.classesTotal}
        </div>
      </div>
      {surname && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          {/* Greyscale while the unit is still in progress */}
          <Portrait
            url={unit.woman?.portraitUrl}
            initial={initialOf(unit.woman?.name)}
            name={unit.woman?.name}
            color={P.midText}
            size={28}
            muted
          />
          <div
            style={{
              fontFamily: oswald,
              fontSize: 8,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: P.midText,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {surname}
          </div>
        </div>
      )}
    </div>
  );
}

// -- Ghost frame: an empty whitewashed spot waiting for its cartaz --
function GhostFrame({ index }: { index: number }) {
  return (
    <div
      aria-hidden
      style={{
        width: CARD_W,
        height: CARD_H,
        border: `2px dashed ${P.lightBorder}`,
        background: '#F9E4E04d',
        borderRadius: 3,
        transform: `rotate(${getRotation(index)}deg)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: oswald,
        fontSize: 20,
        fontWeight: 700,
        color: '#D9C2BD',
      }}
    >
      ?
    </div>
  );
}

// -- Teaser card: the first frame of the first future level, corner torn away --
function TeaserCard({
  teaser,
  label,
}: {
  teaser: NonNullable<MuralLevel['teaser']>;
  label: string;
}) {
  return (
    <div
      style={{
        width: CARD_W,
        height: CARD_H,
        position: 'relative',
        background: P.roseMist,
        borderRadius: 3,
        transform: `rotate(${getRotation(0)}deg)`,
        padding: '12px 9px 9px',
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
        overflow: 'hidden',
        boxShadow: `3px 4px 10px ${P.shadow}`,
      }}
    >
      {/* torn corner */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 26,
          height: 26,
          background: P.charm,
          clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
        }}
      />
      <div
        style={{
          fontFamily: oswald,
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1.25,
          color: '#B99C96',
          textTransform: 'uppercase',
          paddingRight: 14,
        }}
      >
        {label}
      </div>
      <div
        aria-hidden
        style={{ height: 7, width: '70%', background: '#DEC5BF', borderRadius: 2 }}
      />
      <div
        aria-hidden
        style={{ height: 7, width: '50%', background: '#DEC5BF', borderRadius: 2 }}
      />
      {teaser.field && (
        <div
          style={{
            marginTop: 'auto',
            fontFamily: oswald,
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: '#8A6F69',
          }}
        >
          {teaser.field}
          {teaser.years ? ` · ${teaser.years}` : ''}
        </div>
      )}
    </div>
  );
}

// -- Extras shelf: bought journeys, one poster-shaped card each --
const SHELF_CARD_W = 150;
const SHELF_CARD_H = 196;

// -- Pending request: a dashed, stamped placeholder (no progress yet) --
function PendingExtraCard({ extra, onOpen }: { extra: MuralExtra; onOpen: () => void }) {
  const { t } = useTranslation('p2e');
  const gold = '#B8862B';
  return (
    <div
      {...pressable(onOpen)}
      className="mural-shelf-card"
      aria-label={extra.name ?? undefined}
      style={{
        flex: '0 0 auto',
        width: SHELF_CARD_W,
        height: SHELF_CARD_H,
        position: 'relative',
        border: `2px dashed ${gold}`,
        background: P.white,
        color: P.darkText,
        borderRadius: 4,
        boxShadow: `2px 3px 8px ${P.shadow}`,
        padding: '14px 12px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        cursor: 'pointer',
        scrollSnapAlign: 'start',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          fontFamily: oswald,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: gold,
        }}
      >
        {(extra.levelName ? extra.levelName + ' · ' : '') + t('mural.extras.eyebrow')}
      </div>
      <div
        style={{
          fontFamily: dmSerif,
          fontSize: 17,
          lineHeight: 1.15,
          flex: 1,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical',
          color: P.midText,
        }}
      >
        {extra.name ?? extra.productName ?? ''}
      </div>
      {/* The "waiting" stamp, slanted like the store's */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          right: -10,
          top: '44%',
          transform: 'rotate(-12deg)',
          border: `2px dashed ${gold}`,
          color: gold,
          fontFamily: oswald,
          fontWeight: 700,
          fontSize: 9,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          padding: '2px 8px',
          background: 'rgba(255,255,255,0.85)',
          whiteSpace: 'nowrap',
        }}
      >
        {t('mural.extras.pending')}
      </div>
      <div
        style={{
          fontFamily: oswald,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: gold,
          marginTop: 2,
        }}
      >
        {t('mural.extras.pendingHint')}
      </div>
    </div>
  );
}

function ExtraCard({
  extra,
  dateLocale,
  onOpen,
}: {
  extra: MuralExtra;
  dateLocale: typeof enUS;
  onOpen: () => void;
}) {
  const { t } = useTranslation('p2e');
  const total = extra.unitCount > 0 ? extra.unitCount : 1;
  const pct = Math.min(100, Math.round((extra.completedUnits / total) * 100));
  const endsAtLabel = extra.endsAt
    ? format(new Date(extra.endsAt), 'PP', { locale: dateLocale })
    : null;

  // Three moods: expired (grey, read-only), completed (green/gold), live (dark).
  const mood = extra.expired ? 'expired' : extra.completed ? 'completed' : 'live';
  const look =
    mood === 'expired'
      ? {
          bg: '#E9E2E0',
          text: P.warmGray,
          eyebrow: P.warmGray,
          track: 'rgba(0,0,0,0.08)',
          fill: '#B9AEAA',
          footer: P.warmGray,
          shadow: `2px 3px 8px ${P.shadow}`,
        }
      : mood === 'completed'
        ? {
            bg: `linear-gradient(165deg, ${P.green}, #14572F)`,
            text: P.white,
            eyebrow: '#E9CF7A',
            track: 'rgba(255,255,255,0.18)',
            fill: '#E9CF7A',
            footer: '#E9CF7A',
            shadow: '4px 6px 16px rgba(20,87,47,0.28)',
          }
        : {
            bg: P.black,
            text: P.white,
            eyebrow: P.charm,
            track: 'rgba(255,255,255,0.15)',
            fill: P.charm,
            footer: 'rgba(255,255,255,0.75)',
            shadow: '4px 6px 16px rgba(26,16,24,0.28)',
          };

  const footer =
    mood === 'expired'
      ? t('mural.extras.expired')
      : mood === 'completed'
        ? t('mural.extras.completed')
        : endsAtLabel
          ? t('mural.extras.validUntil', { date: endsAtLabel })
          : t('mural.extras.neverExpires');

  return (
    <div
      {...pressable(onOpen)}
      className="mural-shelf-card"
      aria-label={extra.name ?? undefined}
      style={{
        flex: '0 0 auto',
        width: SHELF_CARD_W,
        height: SHELF_CARD_H,
        background: look.bg,
        color: look.text,
        borderRadius: 4,
        boxShadow: look.shadow,
        padding: '14px 12px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        cursor: 'pointer',
        scrollSnapAlign: 'start',
      }}
    >
      <div
        style={{
          fontFamily: oswald,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: look.eyebrow,
        }}
      >
        {(extra.levelName ? extra.levelName + ' · ' : '') + t('mural.extras.eyebrow')}
      </div>
      <div
        style={{
          fontFamily: dmSerif,
          fontSize: 17,
          lineHeight: 1.15,
          flex: 1,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {extra.name ?? extra.productName ?? ''}
      </div>
      <div
        style={{
          fontFamily: oswald,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          opacity: mood === 'expired' ? 0.9 : 0.85,
        }}
      >
        {t('mural.extras.progress', { completed: extra.completedUnits, total: extra.unitCount })}
      </div>
      <div
        aria-hidden
        style={{ height: 4, borderRadius: 2, background: look.track, overflow: 'hidden' }}
      >
        <div style={{ width: `${pct}%`, height: '100%', background: look.fill }} />
      </div>
      <div
        style={{
          fontFamily: oswald,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: look.footer,
          marginTop: 2,
        }}
      >
        {footer}
      </div>
    </div>
  );
}

// -- Dashed "ghost" card at the end of the shelf: the store --
function StoreCard({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation('p2e');
  return (
    <div
      {...pressable(onOpen)}
      className="mural-shelf-card"
      aria-label={t('mural.extras.storeTitle')}
      style={{
        flex: '0 0 auto',
        width: SHELF_CARD_W,
        height: SHELF_CARD_H,
        border: `2px dashed ${P.charm}`,
        background: `${P.baby}66`,
        color: P.maxRed,
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        cursor: 'pointer',
        textAlign: 'center',
        padding: 12,
        scrollSnapAlign: 'start',
      }}
    >
      <div
        aria-hidden
        style={{ fontFamily: oswald, fontSize: 30, fontWeight: 700, lineHeight: 1 }}
      >
        +
      </div>
      <div
        style={{
          fontFamily: oswald,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
        }}
      >
        {t('mural.extras.storeTitle')}
      </div>
      <div style={{ fontFamily: dmSerif, fontSize: 14, color: P.darkText }}>
        {t('mural.extras.storeSubtitle')}
      </div>
    </div>
  );
}

function ExtrasShelf({
  extras,
  isMobile,
  onOpenJourney,
  onOpenStore,
}: {
  extras: MuralExtra[];
  isMobile: boolean;
  onOpenJourney: (journeyId: string) => void;
  onOpenStore: () => void;
}) {
  const { t, i18n } = useTranslation('p2e');
  const dateLocale = i18n.language?.startsWith('pt') ? ptBR : enUS;
  const sidePad = isMobile ? 16 : 24;
  // Requests still waiting for the teacher sit at the end, next to the store.
  const pending = extras.filter((e) => e.status === 'pending');
  const live = extras.filter((e) => e.status !== 'pending');

  return (
    <section
      aria-label={t('mural.extras.title')}
      style={{ maxWidth: 680, margin: '0 auto', padding: `18px 0 6px` }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          padding: `0 ${sidePad}px`,
          marginBottom: 12,
        }}
      >
        <h2
          style={{
            fontFamily: oswald,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: P.darkText,
            margin: 0,
          }}
        >
          {t('mural.extras.title')}
        </h2>
        <span
          {...pressable(onOpenStore)}
          className="mural-header-link"
          style={{
            fontFamily: oswald,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: P.maxRed,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {t('mural.extras.seeProducts')}
        </span>
      </div>
      <div
        className="mural-shelf"
        style={{
          display: 'flex',
          gap: 14,
          overflowX: 'auto',
          padding: `6px ${sidePad}px 14px`,
          scrollSnapType: 'x proximity',
        }}
      >
        {live.map((extra) => (
          <ExtraCard
            key={extra.journeyId}
            extra={extra}
            dateLocale={dateLocale}
            onOpen={() => onOpenJourney(extra.journeyId)}
          />
        ))}
        {pending.map((extra) => (
          <PendingExtraCard key={extra.journeyId} extra={extra} onOpen={onOpenStore} />
        ))}
        <StoreCard onOpen={onOpenStore} />
      </div>
    </section>
  );
}

// -- One level of the street wall --
function LevelSection({
  level,
  isFirstFuture,
  isMobile,
  sectionRef,
  onOpenJourney,
}: {
  level: MuralLevel;
  isFirstFuture: boolean;
  isMobile: boolean;
  sectionRef: (el: HTMLElement | null) => void;
  onOpenJourney: () => void;
}) {
  const { t } = useTranslation('p2e');
  const isFuture = level.status === 'future';
  const isTouched = !isFuture;
  const clickable = isTouched && !!level.journeyId;

  const statusLabel =
    level.status === 'completed'
      ? t('mural.statusComplete')
      : level.status === 'in_progress'
        ? t('mural.statusInProgress')
        : isFirstFuture
          ? t('mural.statusNext')
          : t('mural.statusFuture');

  const statusChipStyle: React.CSSProperties =
    level.status === 'completed'
      ? { background: '#E4F2E9', color: P.green, border: `1px solid ${P.green}44` }
      : level.status === 'in_progress'
        ? { background: P.white, color: P.black, border: `1.5px solid ${P.black}` }
        : isFirstFuture
          ? { background: P.baby, color: P.maxRed, border: `1px solid ${P.charm}` }
          : { background: P.blush, color: P.warmGray, border: `1px solid ${P.lightBorder}` };

  const units = level.units ?? [];

  return (
    <section
      ref={sectionRef}
      style={{
        position: 'relative',
        padding: isMobile ? '30px 16px 34px' : '36px 26px 40px',
        marginBottom: 8,
        borderRadius: 6,
        scrollMarginTop: 96,
        background: isFuture
          ? `repeating-linear-gradient(45deg, ${P.blush}, ${P.blush} 14px, ${P.cream} 14px, ${P.cream} 28px)`
          : undefined,
      }}
    >
      {/* huge level watermark */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -14,
          right: 6,
          fontFamily: oswald,
          fontWeight: 700,
          fontSize: 110,
          lineHeight: 1,
          color: '#F2A0C41c',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {level.levelName ?? ''}
      </div>

      {/* completed stamp across the section */}
      {level.status === 'completed' && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-8deg)',
            border: `3px solid ${P.green}`,
            color: P.green,
            opacity: 0.28,
            padding: '6px 22px',
            fontFamily: oswald,
            fontWeight: 700,
            fontSize: isMobile ? 22 : 30,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 3,
          }}
        >
          {t('mural.levelComplete', { level: level.levelName ?? '' })}
        </div>
      )}

      {/* header row */}
      <div
        {...(clickable ? pressable(onOpenJourney) : {})}
        className={clickable ? 'mural-header-link' : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 10,
          marginBottom: 20,
          position: 'relative',
          zIndex: 2,
          cursor: clickable ? 'pointer' : undefined,
        }}
      >
        <span
          style={{
            fontFamily: oswald,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.12em',
            color: P.white,
            background: isTouched ? P.maxRed : P.warmGray,
            padding: '3px 10px',
            borderRadius: 3,
          }}
        >
          {level.levelName ?? ''}
        </span>
        {level.journeyName && (
          <span
            style={{
              fontFamily: oswald,
              fontSize: 18,
              fontWeight: 700,
              color: isFuture ? P.warmGray : P.darkText,
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
            }}
          >
            {level.journeyName}
          </span>
        )}
        <span
          style={{
            fontFamily: oswald,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            padding: '3px 9px',
            borderRadius: 999,
            ...statusChipStyle,
          }}
        >
          {statusLabel}
        </span>
        {level.isExtra && (
          <span
            style={{
              fontFamily: oswald,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              padding: '2px 7px',
              borderRadius: 999,
              background: 'transparent',
              color: P.warmGray,
              border: `1px dashed ${P.warmGray}`,
            }}
          >
            {t('mural.extras.rowPill')}
          </span>
        )}
      </div>

      {/* mini-cartazes */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '22px 18px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {isTouched
          ? units.map((unit, i) =>
              unit.status === 'completed' ? (
                <CompletedMiniCartaz
                  key={unit.unitId}
                  unit={unit}
                  index={i}
                  onOpen={onOpenJourney}
                />
              ) : unit.status === 'in_progress' ? (
                <InProgressMiniCartaz
                  key={unit.unitId}
                  unit={unit}
                  index={i}
                  onOpen={onOpenJourney}
                />
              ) : (
                <GhostFrame key={unit.unitId} index={i} />
              ),
            )
          : Array.from({ length: level.unitCount }, (_, i) =>
              i === 0 && isFirstFuture && level.teaser ? (
                <TeaserCard
                  key={i}
                  teaser={level.teaser}
                  label={t('mural.whoWillItBe')}
                />
              ) : (
                <GhostFrame key={i} index={i} />
              ),
            )}
      </div>

      {/* future caption */}
      {/* {isFuture && (
        <div
          style={{
            marginTop: 18,
            fontFamily: dmSerif,
            fontStyle: 'italic',
            fontSize: 13,
            color: P.warmGray,
          }}
        >
          {t('mural.cartazesWaiting', { count: level.unitCount })}
        </div>
      )} */}
    </section>
  );
}

// -- Page --
function MuralPage() {
  const { t } = useTranslation('p2e');
  const navigate = useNavigate();
  const w = useWidth();
  const isMobile = w < 480;

  const { data, isLoading } = trpc.progress.getStudentMural.useQuery({});
  const { isEnabled: isFlagEnabled } = useFeatureFlags();
  const showShelf = isFlagEnabled('journey-products');

  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const didInitialScroll = useRef(false);

  // Initial scroll: bring the in-progress level into view once data lands.
  useEffect(() => {
    if (!data || didInitialScroll.current) return;
    const idx = data.levels.findIndex((l) => l.status === 'in_progress');
    if (idx < 0) return;
    const el = sectionRefs.current[idx];
    if (!el) return;
    didInitialScroll.current = true;
    el.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [data]);

  const scrollToLevel = (idx: number) => {
    const el = sectionRefs.current[idx];
    if (!el) return;
    el.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  const openJourney = (level: MuralLevel) => {
    navigate({
      to: '/journey',
      search: { journeyId: level.journeyId ?? undefined, celebrate: undefined, ceremony: undefined },
    });
  };
  const openJourneyById = (journeyId: string) => {
    navigate({ to: '/journey', search: { journeyId, celebrate: undefined, ceremony: undefined } });
  };
  const openStore = () => {
    navigate({ to: '/products' });
  };

  const backLink = (
    <div
      {...pressable(() =>
        navigate({
          to: '/journey',
          search: { journeyId: undefined, celebrate: undefined, ceremony: undefined },
        }),
      )}
      className="mural-header-link"
      style={{
        display: 'inline-block',
        fontFamily: oswald,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        color: P.warmGray,
        cursor: 'pointer',
        marginBottom: 10,
      }}
    >
      {'← ' + t('mural.backToJourney')}
    </div>
  );

  const heading = (
    <h1
      data-testid="mural-heading"
      style={{
        fontFamily: oswald,
        fontSize: isMobile ? 28 : 34,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: P.darkText,
        margin: 0,
      }}
    >
      {t('mural.title')}
    </h1>
  );

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: P.cream,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <style dangerouslySetInnerHTML={{ __html: MURAL_STYLES }} />
        <div
          role="status"
          aria-live="polite"
          style={{
            fontFamily: oswald,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color: P.warmGray,
          }}
        >
          {t('mural.loading')}
        </div>
      </div>
    );
  }

  if (!data || data.levels.length === 0) {
    return (
      <div style={{ minHeight: '100vh', background: P.cream }}>
        <style dangerouslySetInnerHTML={{ __html: MURAL_STYLES }} />
        <div
          style={{
            maxWidth: 680,
            margin: '0 auto',
            padding: isMobile ? '36px 18px' : '48px 24px',
          }}
        >
          {backLink}
          {heading}
        </div>
      </div>
    );
  }

  // "Up next" is the first future section of the path itself, never an extra.
  const firstFutureIdx = data.levels.findIndex((l) => l.status === 'future' && !l.isExtra);
  const { totals } = data;
  const extras = data.extras ?? [];

  // One chip per level (a path may visit a level twice, or add an extra):
  // the chip scrolls to the level's first section and shows its overall state.
  const chips: Array<{
    levelName: string;
    sectionIdx: number;
    status: MuralLevel['status'];
  }> = [];
  data.levels.forEach((level, i) => {
    if (!level.levelName) return;
    const chip = chips.find((c) => c.levelName === level.levelName);
    if (!chip) {
      chips.push({ levelName: level.levelName, sectionIdx: i, status: level.status });
      return;
    }
    if (level.status === 'in_progress') chip.status = 'in_progress';
    else if (level.status === 'future' && chip.status === 'completed') chip.status = 'future';
  });

  return (
    <div style={{ minHeight: '100vh', background: P.cream }}>
      <style dangerouslySetInnerHTML={{ __html: MURAL_STYLES }} />

      {/* Title block */}
      <div
        style={{
          maxWidth: 680,
          margin: '0 auto',
          padding: isMobile ? '30px 18px 14px' : '44px 24px 16px',
        }}
      >
        {backLink}
        {heading}
      </div>

      {/* Sticky summary band */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 40,
          background: 'rgba(255, 248, 245, 0.85)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${P.lightBorder}`,
          padding: '10px 14px',
        }}
      >
        <div
          style={{
            maxWidth: 680,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div
            style={{
              fontFamily: oswald,
              fontSize: 13,
              color: P.darkText,
              letterSpacing: '0.05em',
              textAlign: 'center',
            }}
          >
            <StatSegment
              text={t('mural.statsCartazes', { count: totals.cartazes })}
              highlight={String(totals.cartazes)}
            />
            {' · '}
            <StatSegment
              text={t('mural.statsSelos', { count: totals.selos })}
              highlight={String(totals.selos)}
            />
            {totals.currentLevelName && (
              <>
                {' · '}
                <StatSegment
                  text={t('mural.statsLevel', {
                    level: totals.currentLevelName,
                  })}
                  highlight={totals.currentLevelName}
                />
              </>
            )}
          </div>

          {/* level chips */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            {chips.map((chip) => {
              const chipStyle: React.CSSProperties =
                chip.status === 'completed'
                  ? { background: P.maxRed, color: P.white, border: `1.5px solid ${P.maxRed}` }
                  : chip.status === 'in_progress'
                    ? { background: P.white, color: P.black, border: `1.5px solid ${P.black}` }
                    : { background: P.blush, color: P.warmGray, border: `1.5px solid ${P.lightBorder}` };
              return (
                <button
                  key={chip.levelName}
                  type="button"
                  className="mural-chip"
                  onClick={() => scrollToLevel(chip.sectionIdx)}
                  style={{
                    minWidth: 44,
                    minHeight: 44,
                    padding: '0 10px',
                    borderRadius: 6,
                    fontFamily: oswald,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    cursor: 'pointer',
                    ...chipStyle,
                  }}
                >
                  {(chip.status === 'completed' ? '✓ ' : '') + chip.levelName}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Shelf of bought journeys (journey-products flag) */}
      {showShelf && (
        <ExtrasShelf
          extras={extras}
          isMobile={isMobile}
          onOpenJourney={openJourneyById}
          onOpenStore={openStore}
        />
      )}

      {/* The street wall */}
      <div
        style={{
          maxWidth: 680,
          margin: '0 auto',
          padding: isMobile ? '10px 4px 60px' : '14px 8px 80px',
        }}
      >
        {data.levels.map((level, i) => (
          <LevelSection
            key={`${level.levelId ?? 'no-level'}:${level.journeyId ?? 'none'}:${i}`}
            level={level}
            isFirstFuture={i === firstFutureIdx}
            isMobile={isMobile}
            sectionRef={(el) => {
              sectionRefs.current[i] = el;
            }}
            onOpenJourney={() => openJourney(level)}
          />
        ))}
      </div>
    </div>
  );
}
